import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import ProjectsListCard from '/src/Components/ProjectsListCard/ProjectsListCard';
import { CirclePlus, Pencil, RotateCcw, CheckCheck, Trash2 } from 'lucide-react';


import './style.css';


const editSectionHeader = (title) => {
    return (
        <div className="edit-plan-section-header">
            <h2 className="edit-plan-section-header-title text-align-center">{title}</h2>
        </div>
    )
}


const EditPlanModal = ({
    plan,
    projects,
    handleEditPlanComplete,
    handleOutsideClick,
    handlePlanDelete
}) => {


    const [originalPlan, setOriginalPlan] = useState(plan);
    const [updatedPlan, setUpdatedPlan] = useState(plan);
    const [hasPlanChanged, setHasPlanChanged] = useState(false);


    const handleResetPlan = () => {
        setUpdatedPlan(plan);
        setHasPlanChanged(false);
    }


    const handlePlanNameChange = (value) => {
        setUpdatedPlan({
            ...updatedPlan,
            planName: value
        });
    }

    const handlePlanProjectDIYPreferenceChange = (projectId, diyPreference) => {
        setUpdatedPlan({
            ...updatedPlan,
            projectData: updatedPlan.projectData.map((project) => project.project_id === projectId ? { ...project, diyPreference } : project)
        });
    }

    const handlePlanProjectAddition = (projectId) => {

        const newProject = {
            project_id: projectId,
            diyPreference: 'pro'
        }


        setUpdatedPlan({
            ...updatedPlan,
            projectData: [...updatedPlan.projectData, newProject]
        });
    }

    const handlePlanProjectRemoval = (projectId) => {
        setUpdatedPlan({
            ...updatedPlan,
            projectData: updatedPlan.projectData.filter((project) => project.project_id !== projectId)
        });
    }

    const handlePlanUpdateCompleted = () => {
        if (hasPlanChanged) {
            handleEditPlanComplete(updatedPlan);
        }
        handleOutsideClick?.();
    }

    // useEffect to check if the plan has changed
    useEffect(() => {
        setHasPlanChanged(JSON.stringify(originalPlan) !== JSON.stringify(updatedPlan));
    }, [originalPlan, updatedPlan]);


    // Get the projectsData from the updatedPlan
    const projectData = updatedPlan?.projectData || [];



    // Map through the projectData objects and for each get the full project, keeping diyPreference from the projectData object
    const projectDataWithFullProjects = projectData.map((planProject) => {
        const fullProject = projects.find((p) => p.project_id === planProject.project_id);
        return {
            ...fullProject,
            diyPreference: planProject.diyPreference
        };
    });

    // Get the projects from the projects array that are NOT in projectDataWithFullProjects and add diyPreference 'pro' to each
    const projectsNotInPlan = projects.filter((project) => !projectDataWithFullProjects.some((p) => p.project_id === project.project_id));
    const projectsNotInPlanWithDiyPreference = projectsNotInPlan.map((project) => ({
        ...project,
        diyPreference: 'pro'
    }));

    console.log('projectsNotInPlanWithDiyPreference', projectsNotInPlanWithDiyPreference);

    const popupRef = useRef(null);

    useEffect(() => {
        const handleClick = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                if (hasPlanChanged) {
                    handleEditPlanComplete(updatedPlan);
                }
                handleOutsideClick?.();
            }
        };

        // Add event listener
        document.addEventListener('mousedown', handleClick);

        // Cleanup function
        return () => {
            document.removeEventListener('mousedown', handleClick);
        };
    }, [handleOutsideClick, hasPlanChanged, handleEditPlanComplete, updatedPlan]);

    // Add effect to handle body scroll
    useEffect(() => {
        document.body.style.overflow = 'hidden';

        // Cleanup function to restore scrolling when component unmounts
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const dummyHandleProjectClick = (project) => {
        return;
    }

    const toggleOptions = [
        {
            id: 'diy',
            label: 'You DIY'
        },
        {
            id: 'pro',
            label: 'Hire a Pro'
        }
    ]


    return createPortal(
        <div className="edit-plan-modal-overlay">
            <div className="edit-plan-modal-popup" ref={popupRef}>
                <div className="edit-plan-modal-content">
                    <div className="edit-plan-modal-content-header">
                        <h1>Edit Your Plan</h1>
                        <div className="edit-plan-modal-content-header-buttons">
                            <button
                                className="edit-plan-modal-content-header-button delete"
                                onClick={() => handlePlanDelete(plan.planId)}
                            >
                                <div className="edit-plan-modal-content-header-button-icon-container">
                                    <Trash2 />
                                </div>
                                Delete Plan
                            </button>
                            {hasPlanChanged && (
                                <>
                                    <button
                                        className="edit-plan-modal-content-header-button reset"
                                        onClick={handleResetPlan}
                                    >
                                        <div className="edit-plan-modal-content-header-button-icon-container">
                                            <RotateCcw />
                                        </div>
                                        Reset Plan
                                    </button>
                                    <button className="edit-plan-modal-content-header-button save" onClick={handlePlanUpdateCompleted}>
                                        <div className="edit-plan-modal-content-header-button-icon-container">
                                            <CheckCheck />
                                        </div>
                                        Save Plan
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="edit-plan-sections">
                        {/* Left Column */}
                        <div className="edit-plan-name-section">
                            {editSectionHeader('Change Plan Name')}
                            <div className="edit-plan-name-input-container">
                                <input
                                    type="text"
                                    value={updatedPlan.planName || ''}
                                    onChange={(e) => handlePlanNameChange(e.target.value)}
                                    className="edit-plan-name-input"
                                    placeholder="Enter plan name"
                                />
                            </div>
                        </div>

                        <div className="edit-plan-horizontal-divider"></div>

                        {/* Right Column */}
                        <div className="edit-plan-projects-section">
                            {editSectionHeader('Edit Current Projects')}
                            <div className="edit-plan-current-projects">
                                {projectDataWithFullProjects.map((project) => (
                                    <div key={project.project_id} className="edit-plan-project-card-container">
                                        <ProjectsListCard
                                            project={project}
                                            handleProjectClick={dummyHandleProjectClick}
                                            showAllDetails={false}
                                            simulatorView={true}
                                            isSelected={true}
                                            handleAddingProjectToSimulator={handlePlanProjectAddition}
                                            handleRemovingProjectFromSimulator={handlePlanProjectRemoval}
                                        />
                                        <div className="edit-plan-project-preference-toggle-container">
                                            {toggleOptions.map((option) => {

                                                const isSelected = project.diyPreference === option.id;
                                                return (
                                                    <div
                                                        className={`edit-plan-project-preference-toggle-option ${isSelected ? 'selected' : ''}`}
                                                        // onClick only if the option is not selected
                                                        onClick={() => !isSelected && handlePlanProjectDIYPreferenceChange(project.project_id, option.id)}
                                                    >
                                                        <span className={`edit-plan-project-preference-toggle-option-label ${isSelected ? 'selected' : ''}`}>{option.label}</span>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="edit-plan-horizontal-divider"></div>

                        <div className="edit-plan-projects-section">
                            {editSectionHeader('Add more projects')}
                            <div className="edit-plan-current-projects">
                                {projectsNotInPlanWithDiyPreference.map((project) => (
                                    <ProjectsListCard
                                        key={project.project_id}
                                        project={project}
                                        handleProjectClick={dummyHandleProjectClick}
                                        showAllDetails={true}
                                        simulatorView={true}
                                        isSelected={false}
                                        handleAddingProjectToSimulator={handlePlanProjectAddition}
                                        handleRemovingProjectFromSimulator={handlePlanProjectRemoval}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        ,
        document.body
    );
};

export default EditPlanModal;